
import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';
import { ColumnHeader } from '@/interfaces/components/ColumnHeader';
import headers from './headers';
import { Order } from '@/interfaces/models/Order';
import { TableOptions } from '@/interfaces/components/TableOptions';
import { VenueLabel } from '@/interfaces/models/VenueLabel';
import { Permission } from '@/enums/Permission';
import StatisticFilter from '@/components/statistics/StatisticFilter.vue';
import { Page } from '@/interfaces/api/Page';
import { Article } from '@/interfaces/models/Article';
import PaymentApiService from '@/api/http/PaymentApiService';
import { AxiosResponse } from 'axios';
import { Payment } from '@/interfaces/models/Payment';
import { OrderLog } from '@/interfaces/models/OrderLog';
import OrderLogTable from './OrderLogTable.vue';

@Component({
  components: { StatisticFilter, OrderLogTable },
})
export default class OrderTable extends Vue {
  public payment: Payment | null = null;
  public orderLogs: {
    [key: string]: OrderLog[];
  } = {};

  get accessibleHeaders() {
    return headers.filter((c: ColumnHeader) => {
      if (
        c.value === 'isControlArticle' &&
        this.$can(Permission.ARTICLE_CONTROL_ACTIVATE) &&
        this.$can(Permission.ARTICLE_CONTROL_DEACTIVATE)
      ) {
        return true;
      }
      return c.value !== 'isControlArticle';
    });
  }

  @Prop({ type: Array, required: true }) public venueLabels!: VenueLabel[];
  @Prop({ type: Array, required: true }) public items!: Order[];
  @Prop({ type: Number, required: true }) public total!: number;
  @Prop({ type: Boolean, default: true }) public pagination!: boolean;

  public search: string = '';
  public expanded: string[] = [];
  public options: TableOptions = {};
  public page: number = 1;
  public rowsPerPage: number = 10;
  public sourceHeaders = this.accessibleHeaders;
  public headers: ColumnHeader[] = this.accessibleHeaders.filter((c: ColumnHeader) => c.default);

  public logsVisible: boolean = false;
  public activeOrderLog: OrderLog | null = null;

  get tableHeaders() {
    return this.headers.map((h: ColumnHeader) => ({ ...h, text: this.$t(h.text) }));
  }

  get pageCount() {
    if (this.pagination) {
      return Math.ceil(this.total / this.rowsPerPage);
    }

    return 0;
  }

  public edit(article: Article) {
    if (!article) return;
    if (this.$can(Permission.ARTICLE_UPDATE)) {
      this.$emit('edit', { id: article._id, category: article.category });
    } else {
      this.$emit('simple-edit', { id: article._id, category: article.category });
    }
  }

  public copy(value: string) {
    if (value) {
      navigator.clipboard.writeText(value);
    }
  }

  public showReceipts(order: Order) {
    this.$startLoading('order.receipts');
    const api: PaymentApiService = new PaymentApiService();
    api
      .showPayment({ payment: order.payment })
      .then((res: AxiosResponse<Payment>) => {
        this.payment = res.data;
      })
      .finally(() => {
        this.$stopLoading('order.receipts');
      });
  }

  public showOrderLogs(order: Order) {
    this.$startLoading('order.logs');
    const api: PaymentApiService = new PaymentApiService();
    api
      .showOrderLogs({ ids: [order._id] })
      .then((res: AxiosResponse<OrderLog[]>) => {
        this.orderLogs[order._id] = res.data;
      })
      .finally(() => {
        this.$stopLoading('order.logs');
      });
  }

  public showOrderLog(orderLog: OrderLog) {
    this.logsVisible = true;
    this.activeOrderLog = orderLog;
  }

  public retryCheckoutIntegrationAction(item: any) {
    this.$emit('retry', item);
  }

  @Emit('page')
  @Watch('page')
  public onPageChange(): Page {
    if (this.$route.query.page && parseInt(this.$route.query.page as string, 10) !== this.page) {
      // @ts-ignore
      this.$router.push({ query: { page: this.page, limit: this.rowsPerPage } }).catch((e) => {
        throw Error(`${e}`);
      });
    }

    return { page: this.page, limit: this.rowsPerPage };
  }

  @Emit('change')
  public resetPage() {
    this.options.page = 1;
    return this.options;
  }
}
